/**
 * Named browser failures for Pulse (design doc section 5.4).
 *
 * Each helper sends one closed `code` and, where known, a `flow`, so prod
 * monitoring can say "sign-in is failing for clinic X" without a server request
 * ever having been made: a blank app, a CORS block, Cognito, and the
 * client-side allergy check all fail before or outside our API.
 *
 * PHI rule for this file: messages are fixed strings and only an error's
 * `name` is forwarded. An error's `message` never is: Cognito, fetch and our
 * own API errors can all echo user input (emails, search terms) back into it.
 */

import {
  ClientErrorCode,
  ClientFlow,
  ClientLogLevel,
  flushClientErrors,
  reportClientLog,
} from "./client-error-reporter";

// One report per key per window. A CORS block fails every request on the page
// and an unhandled rejection can fire in a render loop; the first report says
// everything, the next hundred only crowd it out of the capped queue.
const THROTTLE_MS = 60_000;
const lastSent = new Map<string, number>();

function throttled(key: string): boolean {
  const now = Date.now();
  const last = lastSent.get(key);
  if (last !== undefined && now - last < THROTTLE_MS) return true;
  lastSent.set(key, now);
  return false;
}

/** Test seam: forget throttle state between cases. */
export function resetFlowTelemetry(): void {
  lastSent.clear();
  booted = false;
}

/** Error class name only, and only when it looks like one. */
function errorNameOf(err: unknown): string | undefined {
  const name = (err as { name?: unknown } | null | undefined)?.name;
  return typeof name === "string" && /^[A-Za-z][\w.]*$/.test(name) ? name : undefined;
}

// ─── API path to flow ────────────────────────────────────────────────────────
//
// An approximation of app/core/flows.py by first path segment, which is all
// the browser can see. Unknown segments return undefined rather than guess.

const FLOW_BY_SEGMENT: Record<string, ClientFlow> = {
  onboarding: "auth",
  auth: "auth",
  me: "auth",
  rbac: "access",
  patients: "patients",
  appointments: "appointments",
  slots: "appointments",
  queue: "appointments",
  public: "appointments",
  "block-types": "appointments",
  visits: "consultation",
  visit: "consultation",
  prescriptions: "consultation",
  drugs: "consultation",
  scribe: "consultation",
  notes: "consultation",
  sync: "sync",
  billing: "subscription",
  credits: "subscription",
  "platform-invoices": "subscription",
  invoices: "clinic_money",
  "income-entries": "clinic_money",
  "income-sources": "clinic_money",
  expenses: "clinic_money",
  "expense-categories": "clinic_money",
  "recurring-expenses": "clinic_money",
  notifications: "notify",
  webpush: "notify",
  reports: "reports",
  admin: "admin",
  platform: "admin",
  doctors: "admin",
  clinic: "admin",
  clinics: "admin",
  organizations: "admin",
  "clinic-groups": "admin",
  "support-tickets": "admin",
};

export function flowForApiPath(url: string): ClientFlow | undefined {
  let pathname: string;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }
  const segments = pathname.split("/").filter(Boolean);
  const v1 = segments.indexOf("v1");
  const first = v1 >= 0 ? segments[v1 + 1] : segments[0];
  return first ? FLOW_BY_SEGMENT[first] : undefined;
}

// ─── Network blocked ─────────────────────────────────────────────────────────

/**
 * An API request never got an answer while the device says it is online:
 * CORS preflight rejected, DNS, TLS, or the gateway unreachable. None of these
 * reach a server log. A device that knows it is offline is not reported; that
 * is the offline-first path working as designed.
 *
 * The /client-errors POST itself carries only Content-Type, which every
 * gateway allow-list accepts, so it still gets out when a custom header is
 * what the gateway is blocking.
 */
export function reportNetworkBlocked(url: string, err?: unknown): void {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const flow = flowForApiPath(url);
  if (throttled(`NETWORK_BLOCKED:${flow ?? "unknown"}`)) return;
  reportClientLog("ERROR", "api request blocked before reaching the server", errorNameOf(err) ?? "TypeError", undefined, {
    flow,
    code: "NETWORK_BLOCKED",
  });
}

// ─── Cognito ─────────────────────────────────────────────────────────────────

// Failures the user caused and can fix by retyping. Still reported, because a
// spike in them is how a pool-vs-frontend policy drift shows up (2026-08-26),
// but at WARN so they never look like Cognito itself is down.
const USER_INPUT_COGNITO_ERRORS = new Set([
  "NotAuthorizedException",
  "UserNotFoundException",
  "UsernameExistsException",
  "InvalidPasswordException",
  "CodeMismatchException",
  "ExpiredCodeException",
  "EmptySignInUsername",
  "EmptySignInPassword",
  "EmptySignUpUsername",
  "EmptySignUpPassword",
]);

/** A Cognito call failed. Sends the exception name, never the username or email. */
export function reportCognitoFailure(
  code: Extract<ClientErrorCode, "COGNITO_SIGNIN_FAILED" | "COGNITO_SIGNUP_FAILED">,
  err: unknown,
): void {
  const name = errorNameOf(err) ?? "Unknown";
  const level: ClientLogLevel = USER_INPUT_COGNITO_ERRORS.has(name) ? "WARN" : "ERROR";
  const message = code === "COGNITO_SIGNIN_FAILED" ? "cognito sign-in failed" : "cognito sign-up failed";
  reportClientLog(level, message, name, undefined, { flow: "auth", code });
}

// ─── Allergy check ───────────────────────────────────────────────────────────

/**
 * The drug-allergy check had no trustworthy allergy list to run against
 * (core flow 8). A drug BLOCKED by an allergy is the check working and must
 * never be reported here. `reason` is a fixed string chosen by the call site.
 */
export function reportAllergyCheckUnavailable(reason: string, err?: unknown): void {
  reportClientLog("ERROR", `allergy check unavailable: ${reason}`, errorNameOf(err), undefined, {
    flow: "allergy",
    code: "ALLERGY_CHECK_UNAVAILABLE",
  });
}

// ─── Chunk load ──────────────────────────────────────────────────────────────

/**
 * webpack names a failed lazy chunk `ChunkLoadError`. A failed CSS chunk only
 * says so in its message, and a native dynamic import throws a TypeError.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (errorNameOf(err) === "ChunkLoadError") return true;
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return (
    typeof message === "string" &&
    /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/.test(message)
  );
}

/**
 * Report a failed lazy import. Returns true when `err` was one, so a catch
 * that recovers from it can still tell. Usually a deploy replaced the chunk
 * while this tab held the old index.
 */
export function reportChunkLoadFailure(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  if (!throttled("CHUNK_LOAD_FAILED")) {
    reportClientLog("ERROR", "lazy chunk failed to load", errorNameOf(err), undefined, {
      flow: "app_load",
      code: "CHUNK_LOAD_FAILED",
    });
  }
  return true;
}

// ─── Uncaught errors ─────────────────────────────────────────────────────────

// Until the first React commit, any uncaught error means the user is looking
// at a blank page: that is APP_LOAD_FAILED, not a crash inside a working app.
let booted = false;

export function markAppBooted(): void {
  booted = true;
}

function reportUncaught(err: unknown, source: "error" | "rejection"): void {
  if (reportChunkLoadFailure(err)) return;
  const name = errorNameOf(err);
  if (!booted) {
    if (throttled("APP_LOAD_FAILED")) return;
    reportClientLog("ERROR", `app failed to start (${source})`, name, undefined, {
      flow: "app_load",
      code: "APP_LOAD_FAILED",
    });
    // The page is blank and the user is about to give up and close it.
    flushClientErrors();
    return;
  }
  if (throttled(`JS_CRASH:${source}:${name ?? "unknown"}`)) return;
  reportClientLog("ERROR", `uncaught ${source}`, name, undefined, { code: "JS_CRASH" });
}

let installed = false;

/**
 * window `error` and `unhandledrejection`. React 19 routes render errors with
 * no boundary through window.reportError, so those arrive here too.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event: ErrorEvent) => {
    // No error object: an opaque cross-origin "Script error." (Razorpay
    // checkout) or a benign ResizeObserver notice. Nothing actionable in it.
    if (!event.error) return;
    reportUncaught(event.error, "error");
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportUncaught(event.reason, "rejection");
  });
}
